import { useContext, useEffect, useMemo, useState } from "react";
import { useBeforeUnload } from "react-router-dom";

import { getReferenceString } from "@medplum/core";
import { Coding, Coverage, EpisodeOfCare, PlanDefinition, Practitioner } from "@medplum/fhirtypes";
import {
  BillTo,
  DiagnosisCodeableConcept,
  LabOrderInputErrors,
  LabOrderValidationError,
  LabOrganization,
  TestCoding,
} from "@medplum/health-gorilla-core";
import {
  HealthGorillaLabOrderProvider,
  useHealthGorillaLabOrder,
} from "@medplum/health-gorilla-react";
import { DateTimeInput, ResourceInput, useMedplum, ValueSetAutocomplete } from "@medplum/react";

import { Box, Button, Drawer, Flex, Group, Radio, Stack, Textarea, TextInput } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";

import { IconSearch } from "@tabler/icons-react";
import { parseISO } from "date-fns";
import omit from "lodash/omit";

import DisabledReason from "@/components/DisabledReason";
import EpisodeOfCareSearchInput from "@/components/EpisodeOfCareSearchInput";
import { TextWrapper } from "@/components/TextWrapper";
import { PatientContext } from "@/contexts/patient";
import { ICD10ValueSets } from "@/fhir/enums/icd";
import { Systems } from "@/fhir/enums/systems";
import LabOrderTemplateSearchInput from "@/health-gorilla/components/LabOrderTemplateSearchInput";
import { useEpisodeOfCareAutocompleteField } from "@/health-gorilla/hooks/useEpisodeOfCareAutocompleteField";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { useAppDispatch } from "@/store";
import { addLabOrder } from "@/store/slices/lab-orders";
import {
  getCodeableConceptCoding,
  getOrganizationReference,
  getPracticeIdForPatient,
} from "@/utils/medplum";
import { showSystemErrorNotification, toast } from "@/utils/notifications";

import {
  fetchAvailablePerformingLabAccounts,
  fetchCoverages,
  saveLabOrder,
  validatePerformingLab,
} from "../services/labs";
import {
  ALLOWED_REQUESTOR_PERMISSIONS,
  BillingOptions,
  PerformingLabAccounts,
} from "../types/labs";
import { validatePatientCoverages } from "../utils/labs";
import { AddLabOrderFormSchema, AddLabOrderFormType } from "../zod-schemas/labs";

/**
 * Mantine v8 Path<T> recurses through FHIR resource types via
 * extension?: Extension[] → valueCoding → Coding → extension → ... causing tsc to hang.
 * Using Pick with only scalar resource identity fields for the form values type
 * breaks the cycle. Full FHIR objects are cast at service/hook boundaries.
 */
type AddLabOrderFormValues = Omit<
  AddLabOrderFormType,
  "performingLab" | "template" | "requestor" | "episodeOfCare" | "labTests"
> & {
  performingLab: Pick<LabOrganization, "resourceType" | "id"> | undefined;
  template: Pick<PlanDefinition, "resourceType" | "id"> | undefined;
  requestor: Pick<Practitioner, "resourceType" | "id"> | undefined;
  episodeOfCare: Pick<EpisodeOfCare, "resourceType" | "id"> | undefined;
  labTests: Record<string, unknown>;
};

import InsuranceOrderableList from "./InsuranceOrderableList";
import { LabTestCard } from "./LabTestCard";
import LabTestSearchInput from "./LabTestSearchInput";
import PerformingLabSearchInput from "./PerformingLabSearchInput";

interface AddLabOrderDrawerProps {
  opened: boolean;
  onClose: () => void;
  encounterId?: string;
}

const LAB_ORDER_NPI_NAME_ERROR_MESSAGE =
  "Medplum Practitioner missing value for element 'name.given':";

export default function AddLabOrderDrawer({
  opened,
  onClose,
  encounterId,
}: AddLabOrderDrawerProps) {
  const medplum = useMedplum();
  const [
    isLoadingPerformingLab,
    { open: startLoadingPerformingLab, close: stopLoadingPerformingLab },
  ] = useDisclosure(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [healthGorillaError, setHealthGorillaError] = useState<
    { generic?: unknown; validation?: LabOrderInputErrors } | undefined
  >();
  const [orderableInsurances, setOrderableInsurances] = useState<
    Array<{ id: string; name: string; rank: number }>
  >([]);
  const [patientCoverages, setPatientCoverages] = useState<Coverage[]>([]);
  const [performingLabAccounts, setPerformingLabAccounts] = useState<PerformingLabAccounts>({
    accountNumbersByLab: {},
    physicianAccountNumbersByLab: {},
  });
  const { patient } = useContext(PatientContext);
  const dispatch = useAppDispatch();
  const { hasAdminRole, hasProviderRole, hasLabOrderPermission } = usePractitionerPermissions();

  useBeforeUnload((event: BeforeUnloadEvent) => {
    if (isSubmitting) event.preventDefault();
  });

  const form = useForm<AddLabOrderFormValues>({
    initialValues: {
      performingLab: undefined,
      template: undefined,
      labTests: {},
      requestor: undefined,
      episodeOfCare: undefined,
      diagnoses: [],
      billTo: undefined,
      selectedCoverages: [],
      notes: "",
      specimenCollectionTime: undefined,
      encounterId,
    },
    validate: zodResolver(AddLabOrderFormSchema),
  });

  const { episodeOfCareInputProps, handleEpisodeOfCareChange } =
    useEpisodeOfCareAutocompleteField(form);

  // Fetch patient coverages when drawer opens
  useEffect(() => {
    if (!opened || !patient?.id) {
      setOrderableInsurances([]);
      return;
    }
    (async () => {
      const { sortedCoverages, insuranceList } = await fetchCoverages(medplum, patient);
      setPatientCoverages(sortedCoverages);
      setOrderableInsurances(insuranceList);
    })();
  }, [opened, patient, medplum]);

  const practiceId = useMemo(() => getPracticeIdForPatient(patient), [patient]);

  useEffect(() => {
    if (!opened || !patient?.id || !practiceId) {
      return;
    }
    (async () => {
      try {
        startLoadingPerformingLab();
        const performingLabAccounts = await fetchAvailablePerformingLabAccounts(
          medplum,
          practiceId,
        );
        setPerformingLabAccounts(performingLabAccounts);
      } finally {
        stopLoadingPerformingLab();
      }
    })();
  }, [opened, patient, medplum, practiceId, startLoadingPerformingLab, stopLoadingPerformingLab]);

  const healthGorilla = useHealthGorillaLabOrder({
    patient,
    requester: form.values.requestor as Practitioner | undefined,
  });

  const {
    state: healthGorillaState,
    createOrderBundle,
    setPerformingLab,
    setTests,
    setDiagnoses,
    updateBillingInformation,
    setSpecimenCollectedDateTime,
    setOrderNotes,
    setPerformingLabAccountNumber,
  } = healthGorilla;

  const handleSubmit = async () => {
    try {
      if (!hasLabOrderPermission) return;

      const validationResult = form.validate();
      if (validationResult.hasErrors) {
        return;
      }
      setIsSubmitting(true);
      setHealthGorillaError(undefined);
      const serviceRequest = await saveLabOrder(
        medplum,
        form.values as unknown as AddLabOrderFormType,
        createOrderBundle,
        hasProviderRole || hasAdminRole,
        patient!.id!,
        performingLabAccounts.physicianAccountNumbersByLab[
          getReferenceString(form.values.performingLab!)
        ],
      );
      if (serviceRequest) dispatch(addLabOrder(serviceRequest));
      handleClose();
    } catch (error) {
      if (error instanceof LabOrderValidationError && error.errors) {
        setHealthGorillaError({ validation: error.errors });
      }

      if (error instanceof Error && error.message.includes(LAB_ORDER_NPI_NAME_ERROR_MESSAGE)) {
        toast({
          title: "Possible NPI Name (First or/and Last Name) Mismatch",
          message:
            "The requestor name may not match the name registered in the NPI. Please verify the name and try again.",
        });

        return;
      }

      showSystemErrorNotification();
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    setPerformingLab(undefined);
    setTests([]);
    setDiagnoses([]);
    updateBillingInformation({ billTo: undefined });
    setSpecimenCollectedDateTime(undefined);
    setOrderNotes("");
    onClose();
  };

  const handlePerformingLabChange = (value: LabOrganization[]) => {
    const performingLab = value[0];
    form.setFieldValue("performingLab", performingLab);

    if (!performingLab) {
      setPerformingLab(undefined);
      setPerformingLabAccountNumber(undefined);
      return;
    }

    const accountNumber =
      performingLabAccounts.accountNumbersByLab[getReferenceString(performingLab)];

    if (!accountNumber) {
      toast({
        title: "Account number not found for selected Performing Lab",
        message: `Could not find an account number for "${performingLab.name}". Please contact support.`,
        type: "error",
        autoClose: 10000,
      });
      return;
    }

    setPerformingLab(performingLab);
    setPerformingLabAccountNumber(accountNumber);
  };

  const handleDiagnosesChange = (value: Coding[]) => {
    form.setFieldValue("diagnoses", value);
    setDiagnoses(value.map((item) => ({ coding: [item] })) as DiagnosisCodeableConcept[]);
  };

  const handleSelectLabTest = (...value: TestCoding[]) => {
    if (!value.length) return;

    const labTests = value.reduce((acc, item) => ({ ...acc, [item.code]: item }), {});

    form.setFieldValue("labTests", (prev) => {
      const selectedTests = { ...prev, ...labTests };
      setTests(Object.values(selectedTests) as TestCoding[]);
      return selectedTests;
    });
  };

  const handleRemoveLabTest = (labTest: TestCoding) => {
    form.setFieldValue("labTests", (prev) => {
      const selectedTests = omit(prev, labTest.code);
      setTests(Object.values(selectedTests) as TestCoding[]);
      return selectedTests;
    });
  };

  const handleSelectTemplate = async (value: PlanDefinition[]) => {
    try {
      const template = value[0];
      if (!template) return;

      const { performingLab: currentPerformingLab } = form.values;
      const currentPerformingLabFull = currentPerformingLab as LabOrganization | undefined;

      const { isPerformingLabValid, performingLabReference } = validatePerformingLab(
        template,
        currentPerformingLabFull,
        performingLabAccounts.accountNumbersByLab,
      );
      if (!isPerformingLabValid) {
        form.setFieldError(
          "template",
          currentPerformingLabFull?.name
            ? `This template is not available for the performing lab "${currentPerformingLabFull?.name}"`
            : "This template is not available",
        );
        return;
      }

      startLoadingPerformingLab();

      if (!currentPerformingLab) {
        const performingLab = (await medplum.readReference(
          performingLabReference!,
        )) as LabOrganization;

        if (!performingLab) return;

        handlePerformingLabChange([performingLab]);
      }

      form.setFieldValue("template", template);
      handleSelectLabTest(
        ...(
          template.action?.map(
            (action) => getCodeableConceptCoding(action.code?.[0]) as TestCoding,
          ) ?? []
        ).filter(Boolean),
      );
    } finally {
      stopLoadingPerformingLab();
    }
  };

  const handleRemoveTemplate = (item: PlanDefinition) => {
    form.setFieldValue("template", undefined);
    const testCodes =
      item.action?.map(
        (action) => (getCodeableConceptCoding(action.code?.[0]) as TestCoding).code,
      ) ?? [];

    if (!testCodes.length) return;

    form.setFieldValue("labTests", (prev) => {
      const selectedTests = omit(prev, testCodes);
      setTests(Object.values(selectedTests) as TestCoding[]);
      return selectedTests;
    });
  };

  const handleBillToChange = (value: string) => {
    const billTo = value as BillTo;
    form.setFieldValue("billTo", billTo);
    updateBillingInformation({ billTo });

    // Reset selected coverages when changing bill to option
    if (
      billTo !== BillingOptions.Insurance ||
      orderableInsurances.length === 0 ||
      patientCoverages.length === 0
    ) {
      form.setFieldValue("selectedCoverages", []);
      return;
    }

    // Map ordered insurances back to full Coverage resources
    const selectedCoverages = orderableInsurances
      .map((insurance) => patientCoverages.find((coverage) => coverage.id === insurance.id))
      .filter((coverage): coverage is Coverage => !!coverage);

    if (selectedCoverages.length > 0) {
      try {
        const validated = validatePatientCoverages(selectedCoverages);
        updateBillingInformation({
          billTo: BillingOptions.Insurance,
          patientCoverage: validated,
        });
      } catch (error) {
        console.error("Invalid coverage selection", error);
      }
    }
  };

  const handleInsuranceOrderChange = (
    orderedInsurances: Array<{ id: string; name: string; rank: number }>,
  ) => {
    // Update the ordered insurance list
    setOrderableInsurances(orderedInsurances);
    // Extract IDs for form storage
    const insuranceIds = orderedInsurances.map((ins) => ins.id);
    form.setFieldValue("selectedCoverages", insuranceIds);
  };

  const handleNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    form.setFieldValue("notes", value);
    setOrderNotes(value);
  };

  const handleSpecimenCollectionTimeChange = (value: string) => {
    const specimenCollectionTime = parseISO(value);
    form.setFieldValue("specimenCollectionTime", specimenCollectionTime);
    setSpecimenCollectedDateTime(specimenCollectionTime);
  };

  const handleNowClick = () => {
    handleSpecimenCollectionTimeChange(new Date().toISOString());
  };

  const { performingLab: performingLabRaw, labTests, template: templateRaw } = form.values;
  const performingLab = performingLabRaw as LabOrganization | undefined;
  const template = templateRaw as PlanDefinition | undefined;
  const labTestsList = useMemo(() => Object.values(labTests) as TestCoding[], [labTests]);
  const accountsList = useMemo(
    () => Object.keys(performingLabAccounts.accountNumbersByLab),
    [performingLabAccounts],
  );

  return (
    <HealthGorillaLabOrderProvider {...healthGorilla}>
      <Drawer.Root
        trapFocus
        size={512}
        opened={opened}
        position="right"
        onClose={handleClose}
        closeOnEscape={false}
        closeOnClickOutside={false}
      >
        <Drawer.Overlay />
        <Drawer.Content data-testid="lab-order-drawer">
          <Drawer.Header h={64} px="md" bg="gray.0">
            <Flex w="100%" gap="md" align="center" justify="space-between">
              <Flex gap="md" align="center" style={{ flex: 1 }}>
                <Drawer.CloseButton aria-label="Close Add Lab Order Drawer" />
                <Drawer.Title style={{ flex: 1 }}>
                  <TextWrapper fz="lg" variant="bold" style={{ wordBreak: "break-word" }}>
                    Add Lab Order
                  </TextWrapper>
                </Drawer.Title>
              </Flex>
              <Button
                type="button"
                loading={isSubmitting}
                onClick={handleSubmit}
                data-testid="lab-order-submit"
                disabled={!hasLabOrderPermission}
              >
                Submit Order
              </Button>
            </Flex>
          </Drawer.Header>
          <Drawer.Body p="lg">
            <Stack gap="lg">
              <PerformingLabSearchInput
                required
                accounts={accountsList}
                disabled={isSubmitting}
                defaultValue={performingLab}
                isLoading={isLoadingPerformingLab}
                onChange={handlePerformingLabChange}
                canRemove={labTestsList.length === 0 && !template}
                error={form.errors.performingLab as string | undefined}
                tooltipLabel="Please remove all selected tests and templates before changing the performing lab"
              />
              {performingLab ? (
                <LabTestSearchInput
                  required
                  label="Tests or Panels"
                  disabled={isSubmitting}
                  performingLab={performingLab}
                  onChange={handleSelectLabTest}
                  placeholder="Search test or panel"
                  rightSection={<IconSearch size={18} />}
                  error={form.errors.labTests as string | undefined}
                />
              ) : (
                <DisabledReason isBlock reason="Please select a performing lab first">
                  <TextInput
                    required
                    disabled
                    label="Tests or Panels"
                    error={form.errors.labTests}
                    placeholder="Search test or panel"
                  />
                </DisabledReason>
              )}
              <LabOrderTemplateSearchInput
                name="template"
                label="Template"
                defaultValue={template}
                practiceId={practiceId}
                placeholder="Search template"
                performingLab={performingLab}
                {...form.getInputProps("template")}
                accounts={accountsList}
                onChange={handleSelectTemplate}
                onRemove={handleRemoveTemplate}
              />
              <Box>
                <ResourceInput
                  required
                  name="requestor"
                  label="Requestor"
                  resourceType="Practitioner"
                  placeholder="Search requestor"
                  error={form.errors.requestor as string | undefined}
                  defaultValue={form.values.requestor as Practitioner | undefined}
                  onChange={(val) =>
                    form.setFieldValue(
                      "requestor",
                      val as Pick<Practitioner, "resourceType" | "id"> | undefined,
                    )
                  }
                  searchCriteria={{
                    _compartment: getOrganizationReference(practiceId),
                    identifier: `${Systems.HL7NPI}|`,
                    "_has:PractitionerRole:practitioner:role": ALLOWED_REQUESTOR_PERMISSIONS,
                  }}
                />
                <TextWrapper mt={2} size="sm" c="gray.6">
                  Search requires Provider Profile to contain NPI
                </TextWrapper>
              </Box>
              <EpisodeOfCareSearchInput
                {...episodeOfCareInputProps}
                onChange={handleEpisodeOfCareChange}
                error={form.errors.episodeOfCare as string | undefined}
                searchCriteria={{
                  patient: getReferenceString(patient!)!,
                  _compartment: getOrganizationReference(practiceId),
                }}
              />
              {healthGorillaState.selectedTests.map((labTest) => (
                <LabTestCard
                  labTest={labTest}
                  patient={patient}
                  key={labTest.code}
                  onRemove={handleRemoveLabTest}
                  metadata={healthGorillaState.testMetadata[labTest.code]}
                  error={healthGorillaError?.validation?.testMetadata?.[labTest.code]}
                />
              ))}
              <ValueSetAutocomplete
                name="diagnoses"
                label="Diagnoses"
                creatable={false}
                minInputLength={1}
                binding={ICD10ValueSets.Clinical}
                {...form.getInputProps("diagnoses")}
                onChange={handleDiagnosesChange}
              />
              <Radio.Group
                label="Bill to:"
                {...form.getInputProps("billTo", { type: "checkbox" })}
                withAsterisk
                onChange={handleBillToChange}
              >
                <Stack gap="sm">
                  <Radio mt="xs" label="Client" value={BillingOptions.Patient} />
                  <DisabledReason
                    isBlock
                    reason={
                      orderableInsurances.length === 0
                        ? "Client has no insurance information available"
                        : undefined
                    }
                  >
                    <Radio
                      label="Insurance"
                      value={BillingOptions.Insurance}
                      disabled={orderableInsurances.length === 0}
                    />
                  </DisabledReason>
                  <Radio label="Practice" value={BillingOptions.CustomerAccount} />
                </Stack>
              </Radio.Group>

              {form.values.billTo === BillingOptions.Insurance &&
                orderableInsurances.length > 0 && (
                  <InsuranceOrderableList
                    insurances={orderableInsurances}
                    onChange={handleInsuranceOrderChange}
                  />
                )}

              <Textarea
                label="Order Notes"
                {...form.getInputProps("notes")}
                onChange={handleNotesChange}
              />
              <Group w="100%" align="flex-end">
                <Box style={{ flex: 1 }}>
                  <DateTimeInput
                    name="specimenCollectionTime"
                    label="Speciment Collection Time"
                    {...form.getInputProps("specimenCollectionTime")}
                    onChange={handleSpecimenCollectionTimeChange}
                    defaultValue={form.values.specimenCollectionTime?.toISOString()}
                  />
                </Box>
                <Button variant="outline" color="burgundy.9" onClick={handleNowClick}>
                  Now
                </Button>
              </Group>
            </Stack>
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Root>
    </HealthGorillaLabOrderProvider>
  );
}
